import { Component, OnInit, ChangeDetectionStrategy, computed, signal } from '@angular/core';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { NavigationService } from '@app/common-codes/services/navigation.service';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { DataService } from '@app/common-codes/services/DataService';
import { DependencyInjectionProvider } from '@app/common-codes/services/DependecyInjection.provider';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { BuzzerConfig } from '@app/common-codes/models/matwins-tenant/BuzzerConfig.model';
import { TranslationService } from '@app/common-codes/services/TranslationService';

import { VISITOR_TYPES, VisitorType, visibleVisitorTypes as visibleTypesForChannel } from '../../shared/visitor-types';

export { VISITOR_TYPES } from '../../shared/visitor-types';
export type { VisitorType } from '../../shared/visitor-types';

@Component({
  selector: 'app-ipad-visitor-type',
  imports: [TranslatePipe],
  templateUrl: './ipad-visitor-type.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class IPadVisitorTypeComponent implements OnInit {

  activeBuzzers = signal<BuzzerConfig[]>([]);
  pressing = signal<number | null>(null);

  private buzzerService?: DataService<BuzzerConfig>;

  /** Types affichés selon les options du menu activées dans l'admin (PointOfService). */
  visibleVisitorTypes = computed(() =>
    visibleTypesForChannel('ipad', this.tenantService.currentPointOfService())
  );

  constructor(
    private navService: NavigationService,
    private variables: MatwinsVariablesService,
    private tenantService: CurrentTenantService,
    private di: DependencyInjectionProvider,
    private alertModalService: AlertModalService,
    private translationService: TranslationService
  ) { }

  ngOnInit() {
    const pos = this.tenantService.currentPointOfService();
    if (!pos) return;

    if (pos.dS_ShowBuzzer && pos.buzzerConfigs?.length) {
      const now = new Date();
      // R-20-118 : active si elle a commence et n'est pas terminee. Une borne absente n'exclut rien.
      const active = pos.buzzerConfigs.filter(b => {
        if (!b.show) return false;
        if (b.status !== 1) return false;
        if (b.startDate && new Date(b.startDate) > now) return false;
        if (b.endDate && new Date(b.endDate) < now) return false;
        return true;
      });
      this.activeBuzzers.set(active);
      this.buzzerService = this.di.getOrCreateDataService(BuzzerConfig);
    }
  }

  /** Vignette de la sonnette, ou null si elle n'en a pas (repli sur l'icone). */
  getBuzzerImage(buzzer: BuzzerConfig): string | null {
    return buzzer.image?.thumbnail_Path ? `Documents/${buzzer.image.thumbnail_Path}` : null;
  }

  /** Libelle de la langue courante, sinon la premiere traduction disponible. */
  private translationText(translations: { fK_Language: number; translationValue: string }[] | undefined): string {
    if (!translations?.length) return '';

    const langId = this.translationService.currentLanguageId;
    const match = langId != null ? translations.find(t => t.fK_Language === langId) : undefined;
    return (match ?? translations[0])?.translationValue ?? '';
  }

  getBuzzerSubject(buzzer: BuzzerConfig): string {
    return this.translationText(buzzer.subjectTranslations) || 'Buzzer';
  }

  getBuzzerBody(buzzer: BuzzerConfig): string {
    return this.translationText(buzzer.bodyTranslations);
  }

  selectVisitorType(typeKey: string) {
    const route = `/ipad/checkin/${typeKey}`;
    this.navService.navigateTo(route);
  }

  async pressBuzzer(buzzer: BuzzerConfig): Promise<void> {
    if (!this.buzzerService) return;
    this.pressing.set(buzzer.id);
    try {
      await this.buzzerService.execute('POST', `press/${buzzer.id}`);
      const title = buzzer.subjectTranslations?.[0]?.translationValue ?? 'Buzzer';
      this.alertModalService.showToast(`${title} notified`, ToastType.Success);
      this.navService.navigateTo('/ipad/home');
    } catch (err) {
      console.error('Buzzer press failed:', err);
      this.alertModalService.showToast('Failed to send notification', ToastType.Error);
    } finally {
      this.pressing.set(null);
    }
  }

  goBack() {
    this.navService.goBack();
  }

  goForward() {
    // TODO: Implement forward navigation if needed
  }

  goHome() {
    const route = `/ipad/home`;
    this.navService.navigateTo(route);
  }
}
